import { CFG, MODULKATALOG } from './config.js';
import { currentLocale, t } from './i18n.js';
/* ============================================================================
   cloudtec Plattform-Konfigurator — Calculation model
   ----------------------------------------------------------------------------
   Spec-konforme Berechnung nach 01-build-blueprint.md §5:

       SizeBase (CapP)  →  CM = 0.85 + 0.70 × CI/100
       UI (Uncertainty) →  UF = 1.00 + 0.25 × UI/100
       Aufwand          =  CapP × stundenProCapp × CM × UF
       Kosten           =  Aufwand × Mischsatz, ± Konfidenzband (15% + 35% × UI/100)

   Score-Mapping pro Frage: am Ende dieser Datei (siehe Score-Funktionen).
   Konstanten kommen aus config.js (CFG).
   ============================================================================ */

const RATE_HOUR = CFG.mischsatz.chfProStunde;
const RATE_PT   = CFG.mischsatz.chfProPt;
const HOURS_PER_PT = CFG.mischsatz.stundenProPt;

// ---- formatting helpers -----------------------------------------------------
const fmtChf = (v) => {
  if (v == null || !Number.isFinite(v)) return 'CHF —';
  const rounded = Math.round(v / 100) * 100;
  return 'CHF ' + rounded.toLocaleString('de-CH').replace(/,/g, "'");
};
const fmtChfTight = (v) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const rounded = Math.round(v / 100) * 100;
  return rounded.toLocaleString('de-CH').replace(/,/g, "'");
};
const fmtNum = (v) => v == null || !Number.isFinite(v) ? '—' : Math.round(v).toLocaleString('de-CH').replace(/,/g, "'");
const fmtPct = (v) => `${Math.round(v)}%`;

// ---- configuration code (stub) ----------------------------------------------
function _hash32(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}
const _ALPHA = CFG.konfigurationsschluessel.zeichen;
function _block(seed) {
  let s = seed >>> 0;
  let out = '';
  for (let i = 0; i < 4; i++) {
    out += _ALPHA[s % _ALPHA.length];
    s = Math.floor(s / _ALPHA.length) ^ Math.imul(s, 2654435761);
    s = s >>> 0;
  }
  return out;
}
function generateConfigCode(state) {
  if (!state) return null;
  const { configCode, ...rest } = state;
  const json = JSON.stringify(rest);
  if (json === '{}' || json.length < 5) return null;
  const h1 = _hash32(json);
  const h2 = _hash32(json.split('').reverse().join('') + 'salt');
  const h3 = _hash32('cloudtec' + json);
  return 'CT-' + _block(h1) + '-' + _block(h2) + '-' + _block(h3);
}
/**
 * Resolve the locale-specific Calendly base URL.
 */
function calendlyBaseUrl() {
  const locale = (typeof currentLocale === 'function') ? currentLocale() : 'de';
  return locale === 'en' ? CFG.calendly.urlEn : CFG.calendly.urlDe;
}

/**
 * Build a Calendly URL pre-filled with the configurator code (Calendly
 * question `a2`).
 */
function buildCalendlyUrl(state) {
  const base = calendlyBaseUrl();
  if (!state) return base;
  const code = state.configCode || generateConfigCode(state);
  if (!code) return base;
  return base + (base.includes('?') ? '&' : '?') + 'a2=' + encodeURIComponent(code);
}

function safeCalc(state) {
  try { return calc(state); } catch (e) { return null; }
}

// ── JSON Export (frontend-only, no backend round-trip) ───────────────────────
/**
 * Build the structured export payload per 06-export-spec.md §2.2.
 * Includes inputs, derived calculation, TCO, AI metadata, and the standard
 * exclusion list (Annahmen und Ausschlüsse).
 */
function buildExport(state) {
  const r = safeCalc(state);
  return {
    $schema: 'cloudtec-konfigurator-export-v1',
    export_metadata: {
      version: 1,
      erstellt_am: new Date().toISOString(),
      konfigurations_schluessel: state.configCode || generateConfigCode(state),
      konfig_kalibriert: CFG.meta.zuletztKalibriertAm,
    },
    eingaben: {
      pre_step: state.pre,
      seite_1: state.p1,
      seite_2: state.p2,
      seite_3: state.p3,
    },
    berechnung: r ? {
      size_base: r.sizeBase,
      complexity_index: r.complexityIndex,
      complexity_band: r.complexityBand,
      complexity_multiplier: r.complexityMultiplier,
      uncertainty_index: r.uncertaintyIndex,
      uncertainty_factor: r.uncertaintyFactor,
      konfidenzband_prozent: r.konfidenzbandProzent,
      aufwand_stunden: r.aufwandStunden,
      aufwand_pt: r.aufwandPt,
      kosten: r.kosten,
      t_shirt_size: r.tShirtSize?.label,
      phasen: r.phasen,
      top_treiber: r.topTreiber,
      top_unsicherheiten: r.topUnsicherheiten,
      category_scores: r.categoryScores,
    } : null,
    kostenkonfiguration: {
      projektbudget_chf: r?.chosenBudget,
      finanzierungszeitraum_jahre: state.p3.financingYears,
      anzahlung_prozent: state.p3.downPayment,
      weiterentwicklung_prozent: state.p3.growthBudget,
      service_optionen: {
        sla: state.p3.sla,
        seo_checks: state.p3.seoChecks,
        security_scanning: state.p3.securityScanning,
      },
      beratungsoptionen: {
        advisory_design: state.p3.advisoryDesign,
        advisory_spec: state.p3.advisorySpec,
        gesamt_pt: r?.advisoryPT,
        gesamt_chf: r?.advisoryCost,
      },
      fixe_jahreskosten_genutzt: CFG.fixeJahreskosten,
    },
    tco: r ? {
      jahre: r.years,
      einmalige_kosten_chf: r.years[0]?.total ?? 0,
      wiederkehrende_kosten_pro_jahr_chf: r.yearlyRunning,
      tco_3_jahre_chf: r.tco3,
      tco_5_jahre_chf: r.tco5,
    } : null,
    ki_metadata: state.ki_metadata ? {
      ki_used: !!state.ki_metadata.feldConfidence,
      model: state.ki_metadata.model,
      prefilled_at: state.ki_metadata.prefilledAt,
      felder_ki_befuellt_anzahl: Object.keys(state.ki_metadata.feldConfidence || {}).length,
      felder_unchanged_anzahl: kiPrefilledPaths(state).length,
      begruendung: state.ki_metadata.begruendung,
    } : { ki_used: false },
    ausschluesse: [
      'Drittanbieter-Lizenzen (z.B. Stripe, Payrexx)',
      'SMS-Versand als Pass-Through (CHF 0.0008 pro SMS)',
      'Schulung des Kundenteams',
      'Mehrwertsteuer',
      'Cloud-Consumption ausserhalb cloudtec-Hosting',
    ],
  };
}

function downloadJsonExport(state) {
  const payload = buildExport(state);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const slug = (state.pre?.title || 'konfiguration').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'konfiguration';
  const datum = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `cloudtec-plattform-einschaetzung-${slug}-${datum}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── KI-Confidence helpers ────────────────────────────────────────────────────
function _getByPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

/**
 * Returns the confidence level ('high'|'medium'|'low') for a field path
 * (e.g. "p2.compliance") if the AI prefilled it AND the user has not changed
 * the value since. Returns null if the field was never prefilled or has been
 * edited since.
 */
function kiConfidence(state, path) {
  const meta = state?.ki_metadata;
  if (!meta) return null;
  const conf = meta.feldConfidence?.[path];
  if (!conf) return null;
  const snap = meta.aiSnapshot;
  if (!snap) return null;
  const cur = _getByPath(state, path);
  const old = _getByPath(snap, path);
  return JSON.stringify(cur) === JSON.stringify(old) ? conf : null;
}

/** All KI-prefilled paths still unchanged by the user. */
function kiPrefilledPaths(state) {
  const conf = state?.ki_metadata?.feldConfidence;
  if (!conf) return [];
  return Object.keys(conf).filter(p => kiConfidence(state, p) !== null);
}

function hasMeaningfulInput(state) {
  if (!state) return false;
  const { pre = {}, p1 = {}, p2 = {}, p3 = {} } = state;
  return !!(pre.platformType || pre.companyKind || pre.projectKind || pre.title
    || (p1.modules && p1.modules.length) || (p1.hilfs && p1.hilfs.length)
    || (p2.integrations && p2.integrations.length)
    || (p2.compliance && p2.compliance.length)
    || (p2.security && p2.security.length)
    || p2.activeUsers || p3.budget != null);
}

// ---- default state ----------------------------------------------------------
function defaultState() {
  return {
    pre: {
      title: '',
      description: '',
      platformType: null,
      projectKind: null,
      companyKind: null,
      timelineMonths: 6,
    },
    p1: {
      hilfs: [],
      modules: [],
      ai: [],                         // KI-Funktionen (MODULKATALOG.ki)
      stackcms: [],                   // Stack CMS by cloudtec (MODULKATALOG.stackCms)
      customCore: 0,
      customSupport: 0,
      // KI-erkannte Eigenfunktionen (Block 2). Items: { id, label, description, active }
      // Slider customCore/customSupport bleibt die alleinige Berechnungs-Eingabe;
      // diese Listen sind Sync-Tags, die mit den Slidern verknüpft werden.
      customCoreFeatures: [],
      customSupportFeatures: [],
      customComplexity: 'medium',     // easy / medium / complex
      languages: 'none',              // none / two / many
      performance: 'standard',        // standard / latency / strict
      auditability: 'standard',       // standard / extended / regulatory
    },
    p2: {
      integrations: [],
      migration: null,                // 'yes' | 'no' | 'unknown'
      migrationStreams: 0,
      dataQuality: 'medium',
      migrationPath: 'bigbang',
      dataDomains: [],
      sensitivity: 'internal',        // public / internal / personal / sensitive
      activeUsers: null,              // 'small' / 'medium' / 'large' / 'huge' / 'massive'
      peakConcurrency: null,          // '50' / '500' / '5k' / '5k+'
      tenantModel: null,              // 'single' / 'multi-few' / 'multi-mid' / 'multi-many'
      security: [],
      compliance: [],
      dataResidency: 'ch',            // CH-Hosting verpflichtend per default
      availability: null,             // 'standard' / 'redundant' / 'low-downtime' / 'full-ha'
      _unknown: {},
    },
    p3: {
      budget: null,
      financingYears: CFG.finanzierungszeitraum.defaultJahre,
      downPayment: CFG.anzahlung.defaultProzent,
      growthBudget: CFG.weiterentwicklung.defaultProzent,

      sla: ['basis'],                 // [] | ['basis'] | ['weekend'] | ['vip'] | ['weekend','vip']
      seoChecks: false,
      dataProtection: false,
      securityScanning: 'none',       // 'none' | 'yearly' | 'quarterly' | 'monthly'

      advisoryDesign: 'none',
      advisorySpec: 'none',
      advisoryPrototype: 'none',
    },
  };
}

// ============================================================================
// SizeBase (CapP)  — siehe 01-build-blueprint §5.1
// ============================================================================

// Eine Integration zählt nur, wenn ein System eingetragen ist.
// Verhindert dass leere KI-prefilled Rows oder UI-Anlage-Zeilen in die Berechnung fliessen.
function _isValidIntegration(i) {
  return i && typeof i.system === 'string' && i.system.trim().length > 0;
}

function computeIntegrationsgewicht(integrationen) {
  const s = CFG.scoring.integration;
  return (integrationen || []).filter(_isValidIntegration).reduce((sum, i) => {
    const richtung = s.cappDirektionFaktor[i.direction] || 1;
    const krit     = s.kritikalitaetFaktor[i.criticality] || 1;
    return sum + richtung * krit;
  }, 0);
}

function computeDatengewicht(p2) {
  // Spec sieht pro Domäne {volumen, sensitivitaet} vor; im UI gibt es nur eine
  // globale Sensitivität und Domain-IDs ohne Volumen. Approximation: jede Domäne
  // bekommt Volumen `cappVolumenAnnahme`, Sensitivität aus p2.sensitivity.
  const s = CFG.scoring.daten;
  const sens = s.cappSensitivitaetBonus[p2.sensitivity] || 1;
  return (p2.dataDomains || []).length * (s.cappVolumenAnnahme + sens);
}

function computeCapP(state) {
  const { p1, p2 } = state;
  const w = CFG.cappGewichte;

  const moduleCount = (p1.modules || []).length;
  const hilfsCount  = (p1.hilfs   || []).length;
  const customCore  = p1.customCore    || 0;
  const customSup   = p1.customSupport || 0;
  const integGew    = computeIntegrationsgewicht(p2.integrations);
  const datenGew    = computeDatengewicht(p2);
  const migration   = (p2.migration === 'yes') ? (p2.migrationStreams || 0) : 0;

  // KI-Funktionen + Stack CMS: individuelle Gewichte pro Item (Map-Lookup)
  const kiCapP   = (p1.ai       || []).reduce((s, id) => s + (w.kiFunktionen[id] || 0), 0);
  const cmsCapP  = (p1.stackcms || []).reduce((s, id) => s + (w.stackCms[id]     || 0), 0);

  return w.module             * moduleCount
       + w.hilfsfunktionen    * hilfsCount
       + w.customCore         * customCore
       + w.customSupport      * customSup
       + w.integrationsgewicht* integGew
       + w.datengewicht       * datenGew
       + w.migrationsstroeme  * migration
       + kiCapP
       + cmsCapP;
}

// ============================================================================
// CI-Kategorien-Scores (0..4) — siehe Spec 02-config.yaml + 05-test-scenarios.md
// ============================================================================

function _scaleByThresholds(value, thresholds) {
  // thresholds = [t1, t2, t3, t4] for scores 1, 2, 3, 4. Below t1 → 0.
  if (value < thresholds[0]) return 0;
  if (value < thresholds[1]) return 1;
  if (value < thresholds[2]) return 2;
  if (value < thresholds[3]) return 3;
  return 4;
}

function scoreFunktional(s) {
  const c = CFG.scoring.funktional;
  const m  = (s.p1.modules || []).length;
  const cc = s.p1.customCore    || 0;
  const cs = s.p1.customSupport || 0;
  const cBonus = c.customComplexityBonus[s.p1.customComplexity] || 0;
  const raw = m + cc * c.customCoreFaktor + cs * c.customSupportFaktor + cBonus;
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreNichtFunktional(s) {
  const c = CFG.scoring.nichtFunktional;
  const raw = (c.languagesBonus[s.p1.languages]       || 0)
            + (c.performanceBonus[s.p1.performance]   || 0)
            + (c.auditabilityBonus[s.p1.auditability] || 0);
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreIntegration(s) {
  const c = CFG.scoring.integration;
  const ints = (s.p2.integrations || []).filter(_isValidIntegration);
  if (ints.length === 0) return 0;
  const total = ints.reduce((sum, i) => {
    const k = c.kritikalitaetFaktor[i.criticality] || 1;
    const r = c.scoreDirektionFaktor[i.direction]  || 1;
    return sum + k * r;
  }, 0);
  return _scaleByThresholds(total, c.schwellen);
}

function scoreDaten(s) {
  const c = CFG.scoring.daten;
  const domains = (s.p2.dataDomains || []).length;
  const isMigration = s.p2.migration === 'yes';
  const migBonus = isMigration
    ? c.migrationBaseBonus + Math.min(c.migrationStreamMax, (s.p2.migrationStreams || 0) * c.migrationStreamFaktor)
    : 0;
  const sensBonus = c.scoreSensitivitaetBonus[s.p2.sensitivity] || 0;
  // dataQuality + migrationPath wirken nur bei aktiver Migration
  const qualityBonus = isMigration ? (c.dataQualityBonus[s.p2.dataQuality] || 0) : 0;
  const pathBonus    = isMigration ? (c.migrationPathBonus[s.p2.migrationPath] || 0) : 0;
  const raw = domains + migBonus + sensBonus + qualityBonus + pathBonus;
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreNutzerskalierung(s) {
  const c = CFG.scoring.nutzerskalierung;
  const raw = (c.activeUsersBonus[s.p2.activeUsers]         || 0)
            + (c.peakConcurrencyBonus[s.p2.peakConcurrency] || 0)
            + (c.tenantModelBonus[s.p2.tenantModel]         || 0);
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreSicherheit(s) {
  const c = CFG.scoring.sicherheit;
  const sec = s.p2.security || [];
  let raw = sec.length;
  if (sec.includes('standard_login') && sec.length === 1) raw = c.standardLoginAlleinBonus;
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreCompliance(s) {
  const c = CFG.scoring.compliance;
  const reg = (s.p2.compliance || []).filter(r => r !== 'keine');
  const raw = reg.length + (c.residencyBonus[s.p2.dataResidency] || 0);
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreTeamSkill(s) {
  // cloudtec-Plattformerfahrung ist FIX hoch (Hidden-Layer). Domänenerfahrung
  // wird aus Pre-Step + Custom-Komplexität + Mehrsprachigkeit abgeleitet.
  const c = CFG.scoring.teamSkill;
  const raw = (c.projectKindBonus[s.pre.projectKind]         || 0)
            + (c.companyKindBonus[s.pre.companyKind]         || 0)
            + (c.customComplexityBonus[s.p1.customComplexity]|| 0)
            + (c.languagesBonus[s.p1.languages]              || 0);
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreWartung(s) {
  const c = CFG.scoring.wartung;
  const raw = c.availabilityBonus[s.p2.availability] || 0;
  return _scaleByThresholds(raw, c.schwellen);
}

function scoreKi(s) {
  // Summiert itemBonus über alle gewählten KI- und Stack-CMS-Items.
  const c = CFG.scoring.ki;
  const items = [...(s.p1.ai || []), ...(s.p1.stackcms || [])];
  const raw = items.reduce((sum, id) => sum + (c.itemBonus[id] || 0), 0);
  return _scaleByThresholds(raw, c.schwellen);
}

function computeCategoryScores(state) {
  return {
    funktional:       scoreFunktional(state),
    nichtFunktional:  scoreNichtFunktional(state),
    integration:      scoreIntegration(state),
    daten:            scoreDaten(state),
    nutzerskalierung: scoreNutzerskalierung(state),
    sicherheit:       scoreSicherheit(state),
    compliance:       scoreCompliance(state),
    teamSkill:        scoreTeamSkill(state),
    wartung:          scoreWartung(state),
    ki:               scoreKi(state),
  };
}

// ============================================================================
// CI, CM, UI, UF
// ============================================================================

function computeCI(scores) {
  const w = CFG.complexityIndex;
  let ci = 0;
  for (const cat in w) ci += w[cat] * (scores[cat] / 4) * 100;
  return Math.round(ci * 10) / 10;
}

function getCIBand(ci) {
  const b = CFG.complexityBaender;
  if (ci <= b.sehrNiedrigBis) return 'sehr_niedrig';
  if (ci <= b.niedrigBis)     return 'niedrig';
  if (ci <= b.mittelBis)      return 'mittel';
  if (ci <= b.hochBis)        return 'hoch';
  return 'sehr_hoch';
}

function computeCM(ci) {
  return CFG.complexityMultiplier.basis + CFG.complexityMultiplier.faktor * (ci / 100);
}

function countWeissNicht(state) {
  return Object.keys(state.p2._unknown || {}).length;
}

function computeUI(state) {
  const u = CFG.uncertaintyIndex;
  let ui = 0;
  if (state.pre.projectKind === 'green')   ui += u.greenfieldAufschlag;
  if (state.pre.projectKind === 'rebuild') ui += u.rebuildAufschlag;
  ui += countWeissNicht(state) * u.weissNichtProAntwort;
  // Hinweis: agent_low_confidence-Aufschlag hier weggelassen (KI noch nicht
  // implementiert — kommt mit Backend-Phase)
  return Math.min(u.maxUi, Math.round(ui));
}

function computeUF(ui) {
  return CFG.uncertaintyFactor.basis + CFG.uncertaintyFactor.faktor * (ui / 100);
}

function computeBandbreite(ui) {
  const b = CFG.konfidenzband;
  return (b.basisProzent + b.faktorProzent * (ui / 100)) / 100;
}

// ============================================================================
// Aufwand, Kosten, T-Shirt-Size, Phasen
// ============================================================================

function computeAufwand(capP, ci, ui) {
  const cm = computeCM(ci);
  const uf = computeUF(ui);
  const stunden = capP * CFG.baseProduktivitaet.stundenProCapp * cm * uf;
  return { stunden, personentage: stunden / HOURS_PER_PT, cm, uf };
}

function computeKosten(stunden, ui) {
  const point = stunden * RATE_HOUR;
  const band = computeBandbreite(ui);
  return {
    point: Math.round(point),
    low:   Math.round(point * (1 - band)),
    high:  Math.round(point * (1 + band)),
    band,
  };
}

function getTShirtSize(stunden) {
  const tt = (k) => (typeof t === 'function') ? t(`calc_labels.tshirt.${k}`) : null;
  let chosen = CFG.tShirtSizes[CFG.tShirtSizes.length - 1];
  for (const x of CFG.tShirtSizes) {
    if (stunden <= x.bisStunden) { chosen = x; break; }
  }
  return { ...chosen, beschreibung: tt(chosen.label) || chosen.beschreibung };
}

function computePhasen(stunden) {
  const a = CFG.phasenanteile;
  const l = CFG.phasenLabels;
  const tt = (k) => (typeof t === 'function') ? t(`calc_labels.phasen.${k}`) : null;
  const colors = {
    discovery:            'var(--secondary-50)',
    design:               'var(--info-50)',
    build:                'var(--primary-50)',
    integrationMigration: 'var(--tertiary-50)',
    testHardening:        'var(--warning-50)',
    deploymentHypercare:  'var(--success-50)',
  };
  return Object.keys(a).map(id => ({
    id,
    label:    tt(id) || l[id],
    pct:      Math.round(a[id] * 100),
    stunden:  Math.round(stunden * a[id]),
    kostenChf:Math.round(stunden * a[id] * RATE_HOUR),
    color:    colors[id],
  }));
}

// ============================================================================
// Top-Treiber & Top-Unsicherheiten
// ============================================================================

// Resolved at use-time via i18n so changing locale updates labels live.
function _katLabel(key) {
  return (typeof t === 'function') ? t(`calc_labels.kategorie.${key}`) : key;
}

function computeTopTreiber(scores, n = 3) {
  const w = CFG.complexityIndex;
  const list = Object.keys(w)
    .map(cat => ({
      id: cat,
      label: _katLabel(cat),
      beitrag: w[cat] * (scores[cat] / 4) * 100,
      score: scores[cat],
    }))
    .sort((a, b) => b.beitrag - a.beitrag);
  const max = Math.max(1, list[0]?.beitrag || 1);
  return list.slice(0, n).map(d => ({
    ...d,
    weight: d.beitrag,
    pct: Math.round((d.beitrag / max) * 100),
  }));
}

function computeTopUnsicherheiten(state) {
  const tt = (k, p) => (typeof t === 'function') ? t(k, p) : k;
  const items = [];
  if (state.pre.projectKind === 'green')   items.push(tt('calc_labels.uncertainty.greenfield'));
  if (state.pre.projectKind === 'rebuild') items.push(tt('calc_labels.uncertainty.rebuild'));
  const unkCount = countWeissNicht(state);
  if (unkCount > 0) {
    items.push(tt(unkCount === 1 ? 'calc_labels.uncertainty.weiss_nicht_one' : 'calc_labels.uncertainty.weiss_nicht_other', { count: unkCount }));
  }
  if (!state.p2.activeUsers)        items.push(tt('calc_labels.uncertainty.active_users_missing'));
  if (!state.p2.availability)       items.push(tt('calc_labels.uncertainty.availability_missing'));
  return items;
}

// ============================================================================
// TCO + Service-Optionen + Beratungsoptionen (Add-ons, kein Aufwand-Einfluss)
// ============================================================================

function computeTCO(state, recommendedBudget) {
  const obergrenze = CFG.konfiguratorObergrenzeChf;
  const overScope  = recommendedBudget > obergrenze;
  // Cap recommended at configurator max — über-CHF-1M-Vorhaben werden via Edge-Case-Hinweis abgehandelt
  const recommendedCapped = Math.min(recommendedBudget, obergrenze);
  const chosen = (state.p3.budget != null && state.p3.budget > 0)
    ? state.p3.budget
    : recommendedCapped;

  const dp = chosen * (state.p3.downPayment / 100);
  const rest = chosen - dp;
  const restPerYear = (state.p3.downPayment >= 100) ? 0 : (rest / state.p3.financingYears);

  const f = CFG.fixeJahreskosten;
  const yearlyHosting = chosen * (f.hostingProzent / 100);
  const yearlyLicense = chosen * (f.lizenzWartungProzent / 100);
  const yearlyService = chosen * (f.serviceManagementProzent / 100);
  const yearlyGrowth  = chosen * (state.p3.growthBudget / 100);

  // Service-Optionen (Betrieb, jährlich, beeinflussen CapP NICHT)
  // sla ist ein Array: [], ['basis'], ['weekend'], ['vip'] oder ['weekend','vip'].
  // Weekend+VIP kombinierbar; Basis schliesst Weekend/VIP aus (durchgesetzt im UI).
  const slaIds = Array.isArray(state.p3.sla) ? state.p3.sla : (state.p3.sla ? [state.p3.sla] : []);
  const slaFee = slaIds.reduce((sum, id) => sum + (CFG.serviceOptionen.sla[id]?.chfProJahr || 0), 0);
  const seoFee = state.p3.seoChecks ? CFG.serviceOptionen.seoChecks.chfProJahr : 0;
  const dataProtectionFee = state.p3.dataProtection ? CFG.serviceOptionen.dataProtection.chfProJahr : 0;
  const secScanPkg = String(state.p3.securityScanning || 'none');
  const secScanFee = CFG.serviceOptionen.securityScanning.pakete[secScanPkg] || 0;
  const yearlyOps = slaFee + seoFee + dataProtectionFee + secScanFee;

  // Beratungsoptionen (einmalig, Year 0, beeinflussen CapP NICHT)
  const ptDesign    = CFG.beratungsoptionen.advisoryDesign.pakete[state.p3.advisoryDesign]       || 0;
  const ptSpec      = CFG.beratungsoptionen.advisorySpec.pakete[state.p3.advisorySpec]           || 0;
  const ptPrototype = CFG.beratungsoptionen.advisoryPrototype.pakete[state.p3.advisoryPrototype] || 0;
  const advisoryPT = ptDesign + ptSpec + ptPrototype;
  const advisoryCost = advisoryPT * RATE_PT;

  const yearlyRunning = yearlyHosting + yearlyLicense + yearlyService + yearlyGrowth + yearlyOps;

  const years = [];
  for (let y = 0; y <= 5; y++) {
    const isYear0   = y === 0;
    const down      = isYear0 ? dp : 0;
    const buildRest = (!isYear0 && y <= state.p3.financingYears) ? restPerYear : 0;
    const lic       = isYear0 ? 0 : yearlyLicense;
    const serv      = isYear0 ? 0 : yearlyService;
    const host      = isYear0 ? 0 : yearlyHosting;
    const grow      = isYear0 ? 0 : yearlyGrowth;
    const ops       = isYear0 ? 0 : yearlyOps;
    // Beratungsoptionen werden bewusst NICHT in den TCO eingerechnet —
    // sie sind optional und projektunabhängig (Vorprojekt vor dem Build).
    // Der advisoryCost wird separat im Beratungsblock der Übersichts-Seite ausgewiesen.
    const total     = down + buildRest + lic + serv + host + grow + ops;
    years.push({ year: y, down, buildRest, lic, serv, host, grow, ops, total });
  }
  const tco3 = years.slice(0, 4).reduce((s, y) => s + y.total, 0);
  const tco5 = years.reduce((s, y) => s + y.total, 0);

  return {
    chosenBudget: chosen,
    yearlyHosting, yearlyLicense, yearlyService, yearlyGrowth, yearlyOps, yearlyRunning,
    slaFee, seoFee, dataProtectionFee, secScanFee,
    advisoryCost, advisoryPT, advisoryDesignPT: ptDesign, advisorySpecPT: ptSpec, advisoryPrototypePT: ptPrototype,
    years, tco3, tco5,
    overScope, obergrenze,
  };
}

// ============================================================================
// Master — calc(state)
// ============================================================================

function calc(state) {
  const capP    = computeCapP(state);
  const scores  = computeCategoryScores(state);
  const ci      = computeCI(scores);
  const ciBand  = getCIBand(ci);
  const ui      = computeUI(state);
  const { stunden, personentage, cm, uf } = computeAufwand(capP, ci, ui);
  const kosten  = computeKosten(stunden, ui);
  const tShirt  = getTShirtSize(stunden);
  const phasen  = computePhasen(stunden);
  const treiber = computeTopTreiber(scores);
  const unsic   = computeTopUnsicherheiten(state);
  const tco     = computeTCO(state, kosten.point);

  // Map CI-Band to dot count (1..5)
  const dotsByBand = { sehr_niedrig: 1, niedrig: 2, mittel: 3, hoch: 4, sehr_hoch: 5 };

  return {
    // ── Spec output (canonical names) ─────────────────────────────────────
    sizeBase: capP,
    complexityIndex: ci,
    complexityBand:  ciBand,
    complexityMultiplier: cm,
    uncertaintyIndex: ui,
    uncertaintyFactor: uf,
    konfidenzbandProzent: kosten.band * 100,
    aufwandStunden: Math.round(stunden),
    aufwandPt: Math.round(personentage),
    kosten: {
      point_chf: kosten.point,
      low_chf:   kosten.low,
      high_chf:  kosten.high,
      konfidenz_label: CFG.konfidenzband.konfidenzLabel,
    },
    tShirtSize: tShirt,
    phasen,
    topTreiber: treiber,
    topUnsicherheiten: unsic,
    tco,
    categoryScores: scores,

    // ── Backwards-compat (consumed by existing JSX) ────────────────────────
    hours: stunden,
    personentage,
    recommendedBudget: kosten.point,
    chosenBudget: tco.chosenBudget,
    confidenceLow:  kosten.low,
    confidenceHigh: kosten.high,
    bandWidth:      kosten.band,
    cxBand:         tShirt.label,
    cxIndex:        Math.min(100, Math.max(0, Math.round(ci))),
    cxDots:         dotsByBand[ciBand],
    drivers:        treiber,
    allDrivers:     treiber,
    uncertainties:  unsic,
    phases:         phasen,
    yearlyLicense:  tco.yearlyLicense,
    yearlyService:  tco.yearlyService,
    yearlyHosting:  tco.yearlyHosting,
    yearlyGrowth:   tco.yearlyGrowth,
    yearlyOps:      tco.yearlyOps,
    yearlyRunning:  tco.yearlyRunning,
    slaFee:         tco.slaFee,
    seoFee:         tco.seoFee,
    dataProtectionFee: tco.dataProtectionFee,
    secScanFee:     tco.secScanFee,
    advisoryCost:   tco.advisoryCost,
    advisoryPT:     tco.advisoryPT,
    advisoryDesignPT: tco.advisoryDesignPT,
    advisorySpecPT: tco.advisorySpecPT,
    advisoryPrototypePT: tco.advisoryPrototypePT,
    years:          tco.years,
    tco3:           tco.tco3,
    tco5:           tco.tco5,
    overScope:      tco.overScope,
    rates: { hour: RATE_HOUR, pt: RATE_PT },
  };
}

// ============================================================================
// Dev-Mode "Calc Overlay" — Marginal-PT-Helpers
// ----------------------------------------------------------------------------
// Diese Helpers werden vom Dev-Mode-Overlay verwendet, um pro Element den
// marginalen PT-Beitrag anzuzeigen. Logik: clone state, mutiere ein einzelnes
// Feld, re-rechne, liefere die Differenz in Personentagen.
// ============================================================================

const _clone = (typeof structuredClone === 'function')
  ? structuredClone
  : (x) => JSON.parse(JSON.stringify(x));

function _safePt(state) {
  try { return calc(state).aufwandPt; } catch (e) { return 0; }
}

function _getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function _setPath(obj, path, value) {
  const parts = path.split('.');
  const last = parts.pop();
  const parent = parts.reduce((o, k) => (o[k] = o[k] ?? {}), obj);
  parent[last] = value;
  return obj;
}

/** Marginaler PT-Effekt: was passiert, wenn `mutator(clone)` auf den State angewendet wird? */
function ptMarginal(state, mutator) {
  const base = _safePt(state);
  const c = _clone(state);
  const result = mutator(c);
  return _safePt(result || c) - base;
}

/** PT-Effekt, wenn der Pfad auf einen anderen Wert gesetzt wird. */
function ptIfSet(state, path, value) {
  return ptMarginal(state, (c) => _setPath(c, path, value));
}

/** Beitrag eines Listen-Items (drinnen vs draussen). Immer positiv: zeigt was das Item beiträgt. */
function ptOfListItem(state, listPath, itemId) {
  const arr = _getPath(state, listPath) || [];
  const isIn = arr.includes(itemId);
  const c = _clone(state);
  if (isIn) {
    _setPath(c, listPath, arr.filter(x => x !== itemId));
    return _safePt(state) - _safePt(c);
  } else {
    _setPath(c, listPath, [...arr, itemId]);
    return _safePt(c) - _safePt(state);
  }
}

/** Beitrag eines Slider-Wertes (aktueller Wert vs 0). Liefert auch PT pro +1 Einheit. */
function ptOfSlider(state, path) {
  const cur = _getPath(state, path) || 0;
  const c0 = _clone(state); _setPath(c0, path, 0);
  const c1 = _clone(state); _setPath(c1, path, cur + 1);
  const baseAt0 = _safePt(c0);
  const totalNow = _safePt(state) - baseAt0;
  const oneMore  = _safePt(c1) - _safePt(state);
  return { total: totalNow, perUnit: oneMore };
}

/** Beitrag einer Repeater-Zeile (z.B. Integration) — drinnen vs ohne. */
function ptOfRepeaterItem(state, listPath, predicateFn) {
  const arr = _getPath(state, listPath) || [];
  const without = arr.filter(it => !predicateFn(it));
  if (without.length === arr.length) return 0;
  const c = _clone(state);
  _setPath(c, listPath, without);
  return _safePt(state) - _safePt(c);
}

// ---- Umsetzungs-Zeitraum-Schätzung ------------------------------------------
// Realistischer Zeitraum aus dem Build-Budget. Jeder Bucket spannt einen
// Korridor von der vorherigen Schwelle bis zur eigenen — sonst entstünde an
// jeder Schwelle ein unrealistischer Sprung (z.B. 299k = 9 Mt., 300k = 12 Mt.).
// < 150k → 6 · 150–300k → 6–9 · 300–800k → 9–12 · ≥ 800k → 12–15 Monate.
// Der Slider in Schritt 0 (pre.timelineMonths) wird gegen die untere Grenze
// bewertet — wer im Korridor liegt, ist "ok".
function computeTimeline(budgetChf) {
  let realisticMin, realisticMax;
  if (budgetChf < 150000)      { realisticMin = 6;  realisticMax = 6;  }
  else if (budgetChf < 300000) { realisticMin = 6;  realisticMax = 9;  }
  else if (budgetChf < 800000) { realisticMin = 9;  realisticMax = 12; }
  else                         { realisticMin = 12; realisticMax = 15; }
  return { baseMonths: realisticMin, realisticMin, realisticMax };
}

// Vergleich Wunsch vs realistischer Korridor.
// status: 'ok' | 'tight' | 'critical'
function evaluateTimeline(wishMonths, realisticMin, realisticMax) {
  if (wishMonths >= realisticMin) return { status: 'ok',       gapMonths: 0 };
  const gap = realisticMin - wishMonths;
  if (gap <= 2)                   return { status: 'tight',    gapMonths: gap };
  return                                 { status: 'critical', gapMonths: gap };
}

// ---- expose globals ----------------------------------------------------------
export {
  RATE_HOUR, RATE_PT, HOURS_PER_PT,
  defaultState, calc, computeTimeline, evaluateTimeline,
  fmtChf, fmtChfTight, fmtNum, fmtPct,
  generateConfigCode, hasMeaningfulInput, buildCalendlyUrl, calendlyBaseUrl,
  kiConfidence, kiPrefilledPaths,
  buildExport, downloadJsonExport,
  // Score functions exposed for testability
  computeCapP, computeCI, computeCategoryScores, getCIBand,
  computeCM, computeUI, computeUF, computeAufwand, computeKosten,
  getTShirtSize, computePhasen, computeTCO,
  // Dev-Mode Marginal-PT Helpers
  ptMarginal, ptIfSet, ptOfListItem, ptOfSlider, ptOfRepeaterItem,
};
